import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Visibility as ViewIcon, CheckCircle as ReadIcon, RadioButtonUnchecked as UnreadIcon } from "@mui/icons-material";
import axios from "axios";
import { useLocation } from "react-router-dom";

const API_URL = process.env.REACT_APP_PRODUCTION_SERVER || "https://admin.clikkle.com:5100";

function ContactQueries() {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState("");

  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const idFromUrl = queryParams.get("id");

  const getAuthToken = () => localStorage.getItem("accessToken") || "";

  const fetchRows = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: String(page + 1), limit: String(rowsPerPage), appName: "Clikkle_Website", category: "Contact" });
      if (searchQuery) params.append("q", searchQuery);
      const resp = await axios.get(`${API_URL}/api/adminfeedback?${params}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      setRows(resp.data?.data?.rows || []);
      setTotal(resp.data?.data?.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch contact queries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchQuery]);

  useEffect(() => {
    const fetchById = async (id) => {
      try {
        const resp = await axios.get(`${API_URL}/api/adminfeedback/${id}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        if (resp.data?.data) handleView(resp.data.data);
      } catch (err) {
        setError("Failed to fetch details");
      }
    };
    if (idFromUrl) fetchById(idFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idFromUrl]);

  const handleToggleRead = async (id, read) => {
    try {
      await axios.patch(`${API_URL}/api/adminfeedback/${id}`, { read: !read }, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      fetchRows();
    } catch (err) {
      setError("Failed to update status");
    }
  };

  const handleView = (row) => {
    setSelected(row);
    setAdminNote(row?.meta?.adminNote || "");
    if (!row.read) handleToggleRead(row._id, false);
  };

  const handleSaveNote = async () => {
    if (!selected) return;
    try {
      await axios.patch(`${API_URL}/api/adminfeedback/${selected._id}`, { note: adminNote }, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      setSelected(null);
      fetchRows();
    } catch (err) {
      setError("Failed to save note");
    }
  };

  const formatDate = (d) => new Date(d).toLocaleString();
  const truncate = (s, n = 100) => (s?.length <= n ? s : `${s.substring(0, n)}...`);

  const subjects = useMemo(() => {
    const counts = {};
    rows.forEach((r) => {
      const s = r?.meta?.subject || "Unknown";
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [rows]);

  return (
    <Box sx={{ boxShadow: "none", border: `1px solid ${theme.palette.divider}`, p: 4, borderRadius: 2, minHeight: "80vh" }}>
      <Typography variant="h4" fontWeight="bold">Contact Queries</Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>
      )}

      <Box sx={{ p: 1, mb: 2 }}>
        <TextField size="small" label="Search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        <Button size="small" variant="outlined" sx={{ ml: 1 }} onClick={fetchRows} disabled={loading}>Refresh</Button>
      </Box>

      <TableContainer component={Box}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}><CircularProgress /></Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell>{formatDate(r.createdAt)}</TableCell>
                    <TableCell>{r?.meta?.subject || "—"}</TableCell>
                    <TableCell>{r?.meta?.name || "—"}</TableCell>
                    <TableCell>{r.userEmail || "—"}</TableCell>
                    <TableCell>{truncate(r.content)}</TableCell>
                    <TableCell>
                      <Chip label={r.read ? "Read" : "Unread"} size="small" color={r.read ? "default" : "primary"} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleToggleRead(r._id, r.read)} title={r.read ? "Mark as unread" : "Mark as read"}>
                        {r.read ? <ReadIcon /> : <UnreadIcon />}
                      </IconButton>
                      <IconButton size="small" onClick={() => handleView(r)} title="View details">
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </>
        )}
      </TableContainer>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              Contact Details
              <Chip label={selected.read ? "Read" : "Unread"} size="small" color={selected.read ? "default" : "primary"} />
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {[{ label: "Subject", value: selected?.meta?.subject }, { label: "Name", value: selected?.meta?.name }, { label: "Phone", value: selected?.meta?.phone }, { label: "Email", value: selected.userEmail }, { label: "Created At", value: formatDate(selected.createdAt) }].map((f, i) => (
                  <Grid key={i} item xs={6}>
                    <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2, boxShadow: 1 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>{f.label}</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {f.label === "Email" && f.value ? (
                          <a href={`mailto:${f.value}`}>{f.value}</a>
                        ) : (
                          f.value || "—"
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2, boxShadow: 1 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>Message</Typography>
                    <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>{selected.content}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={3} label="Admin Note" value={adminNote} onChange={(e) => setAdminNote(e.target.value)} />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>Close</Button>
              <Button onClick={() => handleToggleRead(selected._id, selected.read)} variant="outlined">Mark as {selected.read ? "Unread" : "Read"}</Button>
              <Button onClick={handleSaveNote} variant="contained">Save Note</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default ContactQueries;